// Browser side of the robot voice chat: speech-to-text (Web Speech), Gemini reply + audio via the server proxy, lip level callback.
import {currentLocale} from '../i18n/translate';
import {translateAssistant} from '../i18n/auto-translate';
import type {Locale} from '../i18n/languages';
import {speechLocales} from './robotLocale';
export type Turn={role:'user'|'assistant';text:string};
type SR=any;
export const speechSupported=()=>typeof window!=='undefined'&&!!((window as any).SpeechRecognition||(window as any).webkitSpeechRecognition);
export function listen(onResult:(text:string)=>void,onEnd:(ok:boolean)=>void,lang=speechLocales[currentLocale()]):{stop:()=>void}{
 const C:SR=(window as any).SpeechRecognition||(window as any).webkitSpeechRecognition;
 if(!C){onEnd(false);return {stop(){}}}
 const rec=new C();rec.lang=lang;rec.interimResults=false;rec.maxAlternatives=1;rec.continuous=false;
 let got=false;
 rec.onresult=(e:any)=>{const t=e.results&&e.results[0]&&e.results[0][0]&&e.results[0][0].transcript;if(t){got=true;onResult(String(t))}};
 rec.onerror=()=>onEnd(false);
 rec.onend=()=>{if(!got)onEnd(false)};
 try{rec.start()}catch{onEnd(false)}
 return {stop(){rec.onresult=null;rec.onerror=null;rec.onend=null;try{rec.abort()}catch{}}};
}
export async function ask(text:string,history:Turn[],locale:Locale=currentLocale(),signal?:AbortSignal):Promise<{reply:string}|{error:string}>{
 try{
  const r=await fetch('/api/robot-talk',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({text,history,locale,language:speechLocales[locale]}),signal});
  const j=await r.json().catch(()=>({}));
  if(r.ok&&j.reply)return {reply:await translateAssistant(String(j.reply),locale,signal,j.locale===locale?locale:'vi')};
  return {error:r.status===402?'credits':'unavailable'};
 }catch{return {error:'offline'}}
}
let ctx:AudioContext|null=null;
let activeAudio:AudioBufferSourceNode|null=null;
/** Plays the reply. Resolves when finished. `onLevel` receives mouth openness 0..1 while speaking. */
export async function speak(text:string,onLevel:(v:number)=>void,signal?:AbortSignal,locale:Locale=currentLocale()):Promise<'gemini'|'browser'|'none'>{
 if(signal?.aborted)return 'none';
 try{
  const r=await fetch('/api/robot-tts',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({text,locale,language:speechLocales[locale]}),signal});
  if(r.ok){
   const buf=await r.arrayBuffer();
   const AC=(window.AudioContext||(window as any).webkitAudioContext) as typeof AudioContext;
   ctx=ctx||new AC();if(ctx.state==='suspended')await ctx.resume();
   const audio=await ctx.decodeAudioData(buf);
   if(signal?.aborted)return 'none';
   const src=ctx.createBufferSource();src.buffer=audio;activeAudio=src;
   const an=ctx.createAnalyser();an.fftSize=512;src.connect(an);an.connect(ctx.destination);
   const data=new Uint8Array(an.frequencyBinCount);let raf=0;
   const loop=()=>{an.getByteTimeDomainData(data);let sum=0;for(const v of data){const d=(v-128)/128;sum+=d*d}onLevel(Math.min(1,Math.sqrt(sum/data.length)*5));raf=requestAnimationFrame(loop)};
   const abort=()=>{try{src.stop()}catch{}};
   await new Promise<void>(res=>{src.onended=()=>res();signal?.addEventListener('abort',abort,{once:true});src.start();loop()});
   signal?.removeEventListener('abort',abort);if(activeAudio===src)activeAudio=null;src.disconnect();an.disconnect();
   cancelAnimationFrame(raf);onLevel(0);return 'gemini';
  }
 }catch{}
 if(signal?.aborted)return 'none';
 // fallback: the browser's own voice (still animates the mouth)
 try{
  const synth=window.speechSynthesis;if(!synth)return 'none';
  const u=new SpeechSynthesisUtterance(text);u.lang=speechLocales[locale];u.rate=1.02;
  const voices=synth.getVoices(),v=voices.find(v=>v.lang.toLowerCase()===u.lang.toLowerCase())||voices.find(v=>v.lang.split('-')[0]===u.lang.split('-')[0]);if(v)u.voice=v;
  let t=0,timer=0;
  let abort=()=>{};
  await new Promise<void>(res=>{abort=()=>{synth.cancel();res()};signal?.addEventListener('abort',abort,{once:true});u.onstart=()=>{timer=window.setInterval(()=>{t+=1;onLevel(0.25+0.6*Math.abs(Math.sin(t*1.7))*(0.6+0.4*Math.random()))},90)};u.onend=()=>res();u.onerror=()=>res();synth.cancel();synth.speak(u)});
  signal?.removeEventListener('abort',abort);
  clearInterval(timer);onLevel(0);return 'browser';
 }catch{return 'none'}
}
export function stopSpeaking(){try{activeAudio?.stop();activeAudio=null;window.speechSynthesis?.cancel()}catch{}}
